/**
 * T1 diagnostics (R&D only): which edge the limbus refinement settled on for each iris, where the refined centre sits against MediaPipe's seed and against the eye's own corners, how open the lids were, and whether any of that changed between the two halves of a capture.
 *
 * Nothing here feeds the measurement. A frame is measured first; these numbers are read afterwards from what that measurement already found, and a failure here only drops the diagnostics. Per-ray and per-frame values stay in memory for one capture and leave it only as summaries: no image, crop, ray sample or landmark list is kept or exported. Eyes are named by their side of the image — A on the left, B on the right — which is not OD/OS.
 */
import type { Vec2 } from '../vec';
import type { CardlessMeasurement } from './frame';
import type { LimbusFit, LimbusProbe, RayRecord, RayStep } from './limbus';
import { pdFromRatio } from './scale';

/** The diagnostics schema: separate from the algorithm version, because the measurement has not changed. r1 was T1 and T2; r2 adds the T3-L reporting fields — the seed's place between the lids, its distance to each lid, sector coverage and reach, and the frame's face width. */
export const DIAGNOSTICS_REVISION = 't1-r2';

/** Runner-up thresholds: a runner-up counts as "within" when its contrast reaches at least (1 − x) of the chosen step's. */
const WITHIN = [0.05, 0.1, 0.2] as const;
/** Elevation bands for the edge sectors: near the eyes' horizontal and far from it, degrees. */
const NEAR_DEG = 25;

export type Sector =
  | 'nasalUpperFar'
  | 'nasalUpperNear'
  | 'nasalLowerNear'
  | 'nasalLowerFar'
  | 'temporalUpperFar'
  | 'temporalUpperNear'
  | 'temporalLowerNear'
  | 'temporalLowerFar';

const SECTORS: readonly Sector[] = [
  'nasalUpperFar',
  'nasalUpperNear',
  'nasalLowerNear',
  'nasalLowerFar',
  'temporalUpperFar',
  'temporalUpperNear',
  'temporalLowerNear',
  'temporalLowerFar',
];

/** Per-ray numbers for one eye in one frame; they live only until the capture is summarised. */
export interface RayTallies {
  rays: number;
  none: number;
  one: number;
  multiple: number;
  withChosen: number;
  chosenContrast: number[];
  chosenRadius: number[];
  runnerUpContrast: number[];
  runnerUpRadius: number[];
  separation: number[];
  gap: number[];
  within: [number, number, number];
  stronger: number;
  nearMissRadius: number[];
  nearMissContrast: number[];
  nearMissRelative: number[];
}

export interface EyeFrameDiagnostics {
  /** The MediaPipe iris this image-side eye came from: A is landmark 468. */
  landmarkIris: 'A' | 'B';
  seedRadiusPx: number;
  refinedRadiusPx: number;
  refinedToSeedRadiusRatio: number;
  /** Positions are px from the frame centre, x right and y down. */
  seedCentre: Vec2;
  refinedCentre: Vec2;
  refinedCentreMinusSeed: Vec2;
  refinedCentreShiftPx: number;
  nasalRadiusFromSeedCentrePx: number | null;
  temporalRadiusFromSeedCentrePx: number | null;
  nasalRadiusFromRefinedCentrePx: number;
  temporalRadiusFromRefinedCentrePx: number;
  rays: RayTallies;
  /** Distance of each kept lateral step from the fitted centre, px (before the blur correction the diameter gets). */
  edgeRadii: number[];
  sectorRadii: Record<Sector, number[]>;
  fitResidualPx: number;
  usableArc: number;
  edgeBlurPx: number;
  edgeContrast: number;
  eyeInnerCorner: Vec2 | null;
  eyeOuterCorner: Vec2 | null;
  eyeWidthPx: number | null;
  irisHorizontalPosition01: number | null;
  seedHorizontalPosition01: number | null;
  irisVerticalPosition01: number | null;
  lidOpeningPx: number | null;
  lidOpeningNormalizedToEyeWidth: number | null;
  opennessToIrisDiameter: number;
  blinkScore: number | null;
  /** MediaPipe's seed centre between the lids at its own place along the eye, 0 at the upper lid and 1 at the lower: the eye's height without the refinement in it. */
  seedVerticalPosition01: number | null;
  /** From the seed centre up to the upper lid and down to the lower lid, px, and the same over the eye width. */
  upperLidToSeedPx: number | null;
  lowerLidToSeedPx: number | null;
  upperLidToSeedNorm: number | null;
  lowerLidToSeedNorm: number | null;
  /** Lateral rays of the refinement's last pass in each sector, and how many of them found a usable step. */
  sectorRays: Record<Sector, { rays: number; usable: number }>;
  /** The furthest elevation from the eyes' horizontal at which a ray still found a usable step, degrees, per side and half; null where none did. */
  usableReachDeg: Record<Reach, number | null>;
}

export type Reach = 'nasalUpper' | 'nasalLower' | 'temporalUpper' | 'temporalLower';
const REACHES: readonly Reach[] = ['nasalUpper', 'nasalLower', 'temporalUpper', 'temporalLower'];

export interface FrameDiagnostics {
  a: EyeFrameDiagnostics;
  b: EyeFrameDiagnostics;
  /** Whether MediaPipe's iris A (landmark 468) was the one on the image's left, as it always has been on real frames. */
  landmarkAIsLeftInImage: boolean;
  ipdOverOuterCanthi: number | null;
  ipdOverInnerCanthi: number | null;
  /** The PD each image-side iris alone would give with the reference iris. */
  pdFromAMm: number;
  pdFromBMm: number;
  /** MediaPipe's face width, px: the scale ruler furthest from the lids. */
  faceWidthPx: number | null;
}

/** One MediaPipe iris with what the measurement found for it; centres in frame px, except the probe's, which are in eye-band px. */
export interface LandmarkEye {
  seed: { centre: Vec2; radius: number };
  contour: readonly Vec2[];
  fit: LimbusFit;
  probe: LimbusProbe;
  /** The existing openness figure: lid opening over the seed's iris diameter. */
  openness: number;
}

export interface FrameDiagnosticsInput {
  frameWidth: number;
  frameHeight: number;
  /** Where the eye band sits in the frame. */
  bandOrigin: Vec2;
  reference: number;
  ipdPx: number;
  /** Unit vector from seed A to seed B, as the refinement used it. */
  axis: Vec2;
  eyes: { a: LandmarkEye; b: LandmarkEye };
  /** MediaPipe's two eye-corner pairs, in either order and either eye. */
  corners: readonly (readonly [Vec2 | null, Vec2 | null])[];
  /** MediaPipe's blink scores, mapped to image sides. */
  blinkLeftInImage: number | null;
  blinkRightInImage: number | null;
  /** MediaPipe's face width, px, as the frame measured it for its own features. */
  faceWidthPx: number | null;
}

const sub = (p: Vec2, q: Vec2): Vec2 => [p[0] - q[0], p[1] - q[1]];
const dot = (p: Vec2, q: Vec2): number => p[0] * q[0] + p[1] * q[1];
const dist = (p: Vec2, q: Vec2): number => Math.hypot(p[0] - q[0], p[1] - q[1]);

function sortedMedian(s: readonly number[]): number {
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2;
}

const medianOf = (xs: readonly number[]): number | null =>
  xs.length ? sortedMedian([...xs].sort((a, b) => a - b)) : null;

function tallies(rays: readonly RayRecord[]): RayTallies {
  const t: RayTallies = {
    rays: 0,
    none: 0,
    one: 0,
    multiple: 0,
    withChosen: 0,
    chosenContrast: [],
    chosenRadius: [],
    runnerUpContrast: [],
    runnerUpRadius: [],
    separation: [],
    gap: [],
    within: [0, 0, 0],
    stronger: 0,
    nearMissRadius: [],
    nearMissContrast: [],
    nearMissRelative: [],
  };
  for (const ray of rays) {
    t.rays += 1;
    const n = (ray.chosen ? 1 : 0) + ray.others.length;
    if (n === 0) t.none += 1;
    else if (n === 1) t.one += 1;
    else t.multiple += 1;
    const chosen = ray.chosen;
    if (chosen) {
      t.withChosen += 1;
      t.chosenContrast.push(chosen.contrast);
      t.chosenRadius.push(chosen.radiusPx);
      const runnerUp = ray.others.reduce<RayStep | null>(
        (best, s) => (!best || s.contrast > best.contrast ? s : best),
        null,
      );
      if (runnerUp) {
        t.runnerUpContrast.push(runnerUp.contrast);
        t.runnerUpRadius.push(runnerUp.radiusPx);
        t.separation.push(chosen.radiusPx - runnerUp.radiusPx);
        t.gap.push(chosen.contrast - runnerUp.contrast);
        const reaches = (x: number) => runnerUp.contrast >= (1 - x) * chosen.contrast;
        if (reaches(WITHIN[0])) t.within[0] += 1;
        if (reaches(WITHIN[1])) t.within[1] += 1;
        if (reaches(WITHIN[2])) t.within[2] += 1;
        if (runnerUp.contrast > chosen.contrast) t.stronger += 1;
      }
    }
    if (ray.outerNearMiss) {
      t.nearMissRadius.push(ray.outerNearMiss.radiusPx);
      t.nearMissContrast.push(ray.outerNearMiss.contrast);
      t.nearMissRelative.push(ray.outerNearMiss.relativeStrength);
    }
  }
  return t;
}

/** Linear interpolation along a polyline of (along, across) points sorted by along; null outside it. */
function across(poly: readonly Vec2[], s: number): number | null {
  if (poly.length < 2 || s < poly[0]![0] || s > poly[poly.length - 1]![0]) return null;
  for (let i = 1; i < poly.length; i += 1) {
    const p = poly[i - 1]!;
    const q = poly[i]!;
    if (s <= q[0])
      return q[0] === p[0] ? p[1] : p[1] + ((q[1] - p[1]) * (s - p[0])) / (q[0] - p[0]);
  }
  return null;
}

/** A sector's name from which side of the iris a direction points and how far above (+) or below (−) the eyes' horizontal, degrees. */
const sectorOf = (nasal: boolean, elevationDeg: number): Sector =>
  `${nasal ? 'nasal' : 'temporal'}${elevationDeg >= 0 ? 'Upper' : 'Lower'}${Math.abs(elevationDeg) < NEAR_DEG ? 'Near' : 'Far'}` as Sector;

/** Where a point sits between the lids of one eye, how open they are there, and how far the point is from each lid; null where the contour cannot say. */
function lidPosition(
  contour: readonly Vec2[],
  inner: Vec2,
  u: Vec2,
  point: Vec2,
): { position01: number; openingPx: number; upperPx: number; lowerPx: number } | null {
  if (contour.length !== 16) return null;
  const n0: Vec2 = [-u[1], u[0]];
  const n: Vec2 = n0[1] >= 0 ? n0 : [-n0[0], -n0[1]];
  const project = (p: Vec2): Vec2 => [dot(sub(p, inner), u), dot(sub(p, inner), n)];
  const byAlong = (pts: Vec2[]) => pts.map(project).sort((x, y) => x[0] - y[0]);
  const one = byAlong(contour.slice(0, 9) as Vec2[]);
  const two = byAlong([...contour.slice(8, 16), contour[0]!] as Vec2[]);
  const meanAcross = (poly: Vec2[]) => poly.reduce((t, p) => t + p[1], 0) / poly.length;
  const [upper, lower] = meanAcross(one) <= meanAcross(two) ? [one, two] : [two, one];
  const [s, q] = project(point);
  const up = across(upper, s);
  const low = across(lower, s);
  if (up === null || low === null || low - up <= 1) return null;
  return {
    position01: (q - up) / (low - up),
    openingPx: low - up,
    upperPx: q - up,
    lowerPx: low - q,
  };
}

function eyeDiagnostics(
  input: FrameDiagnosticsInput,
  which: 'A' | 'B',
  corners: readonly [Vec2, Vec2] | null,
  otherSeed: Vec2,
  blink: number | null,
): EyeFrameDiagnostics {
  const eye = which === 'A' ? input.eyes.a : input.eyes.b;
  const centre: Vec2 = [input.frameWidth / 2, input.frameHeight / 2];
  const refinedRadiusPx = eye.fit.horizontalDiameterPx / 2;
  const seedCentre = sub(eye.seed.centre, centre);
  const refinedCentre = sub(eye.fit.centre, centre);
  const refinedCentreMinusSeed = sub(eye.fit.centre, eye.seed.centre);
  // The refinement's rays run along the axis from iris A to iris B, so towards the other eye is +1 for A and −1 for B.
  const nasalSide = which === 'A' ? 1 : -1;
  const seedBand = sub(eye.seed.centre, input.bandOrigin);
  const fitBand = sub(eye.fit.centre, input.bandOrigin);
  const nasalKept = eye.probe.kept.filter((k) => k.side === nasalSide);
  const temporalKept = eye.probe.kept.filter((k) => k.side !== nasalSide);
  const down0: Vec2 = [-input.axis[1], input.axis[0]];
  const down: Vec2 = down0[1] >= 0 ? down0 : [-down0[0], -down0[1]];
  const edgeRadii: number[] = [];
  const sectorRadii = Object.fromEntries(SECTORS.map((s) => [s, [] as number[]])) as Record<
    Sector,
    number[]
  >;
  for (const k of eye.probe.kept) {
    const v = sub(k.point, fitBand);
    const r = Math.hypot(v[0], v[1]);
    edgeRadii.push(r);
    const elevation = (Math.atan2(-dot(v, down), Math.abs(dot(v, input.axis))) * 180) / Math.PI;
    sectorRadii[sectorOf(k.side === nasalSide, elevation)].push(r);
  }
  // Which ray angles the lids left usable: every lateral ray of the last pass, counted by sector, and the furthest one that still found a step.
  const sectorRays = Object.fromEntries(SECTORS.map((s) => [s, { rays: 0, usable: 0 }])) as Record<
    Sector,
    { rays: number; usable: number }
  >;
  const usableReachDeg = Object.fromEntries(REACHES.map((k) => [k, null])) as Record<
    Reach,
    number | null
  >;
  for (const ray of eye.probe.rays) {
    const nasal = ray.side === nasalSide;
    const sector = sectorRays[sectorOf(nasal, ray.elevationDeg)];
    sector.rays += 1;
    if (!ray.chosen) continue;
    sector.usable += 1;
    const reach: Reach = `${nasal ? 'nasal' : 'temporal'}${ray.elevationDeg >= 0 ? 'Upper' : 'Lower'}`;
    const far = Math.abs(ray.elevationDeg);
    const seenSoFar = usableReachDeg[reach];
    if (seenSoFar === null || far > seenSoFar) usableReachDeg[reach] = far;
  }
  let inner: Vec2 | null = null;
  let outer: Vec2 | null = null;
  if (corners) {
    [inner, outer] =
      dist(corners[0], otherSeed) <= dist(corners[1], otherSeed)
        ? [corners[0], corners[1]]
        : [corners[1], corners[0]];
  }
  const width = inner && outer ? dist(inner, outer) : null;
  const along01 = (p: Vec2): number | null =>
    inner && outer && width ? dot(sub(p, inner), sub(outer, inner)) / (width * width) : null;
  const alongEye: Vec2 | null =
    inner && outer && width ? [(outer[0] - inner[0]) / width, (outer[1] - inner[1]) / width] : null;
  const lid = inner && alongEye ? lidPosition(eye.contour, inner, alongEye, eye.fit.centre) : null;
  const seedLid =
    inner && alongEye ? lidPosition(eye.contour, inner, alongEye, eye.seed.centre) : null;
  const sideRadius = eye.fit.sideRadiusPx;
  return {
    landmarkIris: which,
    seedRadiusPx: eye.seed.radius,
    refinedRadiusPx,
    refinedToSeedRadiusRatio: refinedRadiusPx / eye.seed.radius,
    seedCentre,
    refinedCentre,
    refinedCentreMinusSeed,
    refinedCentreShiftPx: Math.hypot(refinedCentreMinusSeed[0], refinedCentreMinusSeed[1]),
    nasalRadiusFromSeedCentrePx: medianOf(nasalKept.map((k) => dist(k.point, seedBand))),
    temporalRadiusFromSeedCentrePx: medianOf(temporalKept.map((k) => dist(k.point, seedBand))),
    nasalRadiusFromRefinedCentrePx: which === 'A' ? sideRadius.forward : sideRadius.backward,
    temporalRadiusFromRefinedCentrePx: which === 'A' ? sideRadius.backward : sideRadius.forward,
    rays: tallies(eye.probe.rays),
    edgeRadii,
    sectorRadii,
    fitResidualPx: eye.fit.residualPx,
    usableArc: eye.fit.lateralArc.both,
    edgeBlurPx: eye.fit.blurSigmaPx,
    edgeContrast: eye.fit.contrast,
    eyeInnerCorner: inner ? sub(inner, centre) : null,
    eyeOuterCorner: outer ? sub(outer, centre) : null,
    eyeWidthPx: width,
    irisHorizontalPosition01: along01(eye.fit.centre),
    seedHorizontalPosition01: along01(eye.seed.centre),
    irisVerticalPosition01: lid?.position01 ?? null,
    lidOpeningPx: lid?.openingPx ?? null,
    lidOpeningNormalizedToEyeWidth: lid && width ? lid.openingPx / width : null,
    opennessToIrisDiameter: eye.openness,
    blinkScore: blink,
    seedVerticalPosition01: seedLid?.position01 ?? null,
    upperLidToSeedPx: seedLid?.upperPx ?? null,
    lowerLidToSeedPx: seedLid?.lowerPx ?? null,
    upperLidToSeedNorm: seedLid && width ? seedLid.upperPx / width : null,
    lowerLidToSeedNorm: seedLid && width ? seedLid.lowerPx / width : null,
    sectorRays,
    usableReachDeg,
  };
}

/** One accepted frame's diagnostics, image-side. It reads the finished measurement and changes nothing. */
export function frameDiagnostics(input: FrameDiagnosticsInput): FrameDiagnostics {
  const { a, b } = input.eyes;
  // Each iris gets the corner pair nearest its seed, whatever order MediaPipe's pairs come in.
  const pairs = input.corners.filter((c): c is readonly [Vec2, Vec2] => !!c[0] && !!c[1]);
  const mid = (c: readonly [Vec2, Vec2]): Vec2 => [
    (c[0][0] + c[1][0]) / 2,
    (c[0][1] + c[1][1]) / 2,
  ];
  const nearest = (seed: Vec2, except?: readonly [Vec2, Vec2]) =>
    pairs
      .filter((c) => c !== except)
      .reduce<readonly [Vec2, Vec2] | null>(
        (best, c) => (!best || dist(mid(c), seed) < dist(mid(best), seed) ? c : best),
        null,
      );
  const cornersA = nearest(a.seed.centre);
  const cornersB = cornersA ? nearest(b.seed.centre, cornersA) : nearest(b.seed.centre);
  const aIsLeft = a.fit.centre[0] <= b.fit.centre[0];
  const eyeA = eyeDiagnostics(
    input,
    'A',
    cornersA,
    b.seed.centre,
    aIsLeft ? input.blinkLeftInImage : input.blinkRightInImage,
  );
  const eyeB = eyeDiagnostics(
    input,
    'B',
    cornersB,
    a.seed.centre,
    aIsLeft ? input.blinkRightInImage : input.blinkLeftInImage,
  );
  const [left, right] = aIsLeft ? [eyeA, eyeB] : [eyeB, eyeA];
  const canthi = (pick: (e: EyeFrameDiagnostics) => Vec2 | null): number | null => {
    const p = pick(left);
    const q = pick(right);
    return p && q ? dist(p, q) : null;
  };
  const outerCanthi = canthi((e) => e.eyeOuterCorner);
  const innerCanthi = canthi((e) => e.eyeInnerCorner);
  return {
    a: left,
    b: right,
    landmarkAIsLeftInImage: aIsLeft,
    ipdOverOuterCanthi: outerCanthi ? input.ipdPx / outerCanthi : null,
    ipdOverInnerCanthi: innerCanthi ? input.ipdPx / innerCanthi : null,
    pdFromAMm: pdFromRatio(input.ipdPx, 2 * left.refinedRadiusPx, input.reference),
    pdFromBMm: pdFromRatio(input.ipdPx, 2 * right.refinedRadiusPx, input.reference),
    faceWidthPx: input.faceWidthPx,
  };
}

/** Median, spread and tails of one quantity: the only shape per-frame and per-ray values leave a capture in. */
export interface DiagStat {
  n: number;
  median: number;
  mad: number;
  /** 1.4826 × MAD. */
  robustSd: number;
  p10: number;
  p90: number;
}

function quantileSorted(s: readonly number[], q: number): number {
  const pos = (s.length - 1) * q;
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  return s[lo]! + (s[hi]! - s[lo]!) * (pos - lo);
}

export function diagStat(xs: readonly (number | null | undefined)[]): DiagStat | null {
  const s = xs
    .filter((x): x is number => typeof x === 'number' && Number.isFinite(x))
    .sort((a, b) => a - b);
  if (!s.length) return null;
  const median = sortedMedian(s);
  const mad = sortedMedian(s.map((x) => Math.abs(x - median)).sort((a, b) => a - b));
  return {
    n: s.length,
    median,
    mad,
    robustSd: 1.4826 * mad,
    p10: quantileSorted(s, 0.1),
    p90: quantileSorted(s, 0.9),
  };
}

const med = (xs: readonly (number | null | undefined)[]): number | null =>
  diagStat(xs)?.median ?? null;
const share = (part: number, whole: number): number | null => (whole ? part / whole : null);

export interface EdgeAmbiguity {
  /** Lateral rays of the refinement's last pass, over all used frames. */
  rayCount: number;
  raysWithNoCandidate: number;
  raysWithOneCandidate: number;
  raysWithMultipleCandidates: number;
  multipleCandidateShare: number | null;
  chosenStepContrast: { median: number; p10: number; p90: number } | null;
  runnerUpStepContrast: { median: number; p10: number; p90: number } | null;
  chosenVsRunnerUpContrastGap: { median: number; p10: number } | null;
  chosenRadiusPx: { median: number; robustSd: number } | null;
  runnerUpRadiusPx: { median: number; robustSd: number } | null;
  candidateRadiusSeparationPx: { median: number; p90: number } | null;
  /** Of the rays with a chosen step: the share whose runner-up reaches at least 95, 90 and 80% of its contrast. */
  fractionWhereRunnerUpIsWithin: {
    pct5: number | null;
    pct10: number | null;
    pct20: number | null;
  };
  fractionRunnerUpStronger: number | null;
  outerNearMiss: {
    share: number | null;
    radiusPx: number | null;
    contrast: number | null;
    relativeStrength: number | null;
  };
}

function ambiguity(ts: readonly RayTallies[]): EdgeAmbiguity {
  const sum = (f: (t: RayTallies) => number) => ts.reduce((s, t) => s + f(t), 0);
  const pool = (f: (t: RayTallies) => number[]) => ts.flatMap(f);
  const rays = sum((t) => t.rays);
  const withChosen = sum((t) => t.withChosen);
  const three = (xs: number[]) => {
    const s = diagStat(xs);
    return s ? { median: s.median, p10: s.p10, p90: s.p90 } : null;
  };
  const gap = diagStat(pool((t) => t.gap));
  const chosenR = diagStat(pool((t) => t.chosenRadius));
  const runnerR = diagStat(pool((t) => t.runnerUpRadius));
  const sep = diagStat(pool((t) => t.separation));
  return {
    rayCount: rays,
    raysWithNoCandidate: sum((t) => t.none),
    raysWithOneCandidate: sum((t) => t.one),
    raysWithMultipleCandidates: sum((t) => t.multiple),
    multipleCandidateShare: share(
      sum((t) => t.multiple),
      rays,
    ),
    chosenStepContrast: three(pool((t) => t.chosenContrast)),
    runnerUpStepContrast: three(pool((t) => t.runnerUpContrast)),
    chosenVsRunnerUpContrastGap: gap ? { median: gap.median, p10: gap.p10 } : null,
    chosenRadiusPx: chosenR ? { median: chosenR.median, robustSd: chosenR.robustSd } : null,
    runnerUpRadiusPx: runnerR ? { median: runnerR.median, robustSd: runnerR.robustSd } : null,
    candidateRadiusSeparationPx: sep ? { median: sep.median, p90: sep.p90 } : null,
    fractionWhereRunnerUpIsWithin: {
      pct5: share(
        sum((t) => t.within[0]),
        withChosen,
      ),
      pct10: share(
        sum((t) => t.within[1]),
        withChosen,
      ),
      pct20: share(
        sum((t) => t.within[2]),
        withChosen,
      ),
    },
    fractionRunnerUpStronger: share(
      sum((t) => t.stronger),
      withChosen,
    ),
    outerNearMiss: {
      share: share(pool((t) => t.nearMissRadius).length, rays),
      radiusPx: med(pool((t) => t.nearMissRadius)),
      contrast: med(pool((t) => t.nearMissContrast)),
      relativeStrength: med(pool((t) => t.nearMissRelative)),
    },
  };
}

export interface EyeCaptureDiagnostics {
  /** Share of used frames in which this image-side eye was MediaPipe's iris A (landmark 468). */
  fromLandmarkIrisAShare: number;
  seedRadiusPx: DiagStat | null;
  refinedRadiusPx: DiagStat | null;
  refinedToSeedRadiusRatio: DiagStat | null;
  seedCentreX: DiagStat | null;
  seedCentreY: DiagStat | null;
  refinedCentreX: DiagStat | null;
  refinedCentreY: DiagStat | null;
  refinedCentreMinusSeedX: DiagStat | null;
  refinedCentreMinusSeedY: DiagStat | null;
  refinedCentreShiftPx: DiagStat | null;
  nasalRadiusFromSeedCentrePx: DiagStat | null;
  temporalRadiusFromSeedCentrePx: DiagStat | null;
  nasalRadiusFromRefinedCentrePx: DiagStat | null;
  temporalRadiusFromRefinedCentrePx: DiagStat | null;
  /** Nasal minus temporal radius, per frame, from the refined centre. */
  nasalMinusTemporalPx: DiagStat | null;
  nasalMinusTemporalFromSeedPx: DiagStat | null;
  edgeAmbiguity: EdgeAmbiguity;
  edgeDistribution: {
    fitResidualPx: DiagStat | null;
    usableArc: DiagStat | null;
    edgeBlurPx: DiagStat | null;
    edgeContrast: DiagStat | null;
    /** Every kept lateral step's distance from its frame's fitted centre, pooled over the used frames. */
    edgeRadiusPx: DiagStat | null;
    /** Each frame's own robust SD of those distances, across frames. */
    withinFrameRobustSdPx: DiagStat | null;
    sectorMedianRadiusPx: Record<Sector, number | null>;
    /** Largest minus smallest sector median, where a sector has at least 10 steps. */
    sectorRangePx: number | null;
    sectorRangeVsRadius: number | null;
  };
  eyePosition: {
    eyeInnerCornerX: DiagStat | null;
    eyeInnerCornerY: DiagStat | null;
    eyeOuterCornerX: DiagStat | null;
    eyeOuterCornerY: DiagStat | null;
    eyeWidthPx: DiagStat | null;
    irisHorizontalPosition01: DiagStat | null;
    seedHorizontalPosition01: DiagStat | null;
    irisVerticalPosition01: DiagStat | null;
    /** The seed centre's height between the lids: the eye's own vertical position, free of the refinement. */
    seedVerticalPosition01: DiagStat | null;
  };
  lid: {
    lidOpeningPx: DiagStat | null;
    lidOpeningNormalizedToEyeWidth: DiagStat | null;
    blinkScore: DiagStat | null;
    opennessToIrisDiameter: DiagStat | null;
    upperLidToSeedPx: DiagStat | null;
    lowerLidToSeedPx: DiagStat | null;
    upperLidToSeedNorm: DiagStat | null;
    lowerLidToSeedNorm: DiagStat | null;
  };
  visibility: {
    /** Share of the sector's lateral rays, over every used frame, that found a usable step; 0 means the lids left none. */
    sectorCoverage: Record<Sector, number | null>;
    /** Lateral rays counted in each sector over every used frame. */
    sectorRays: Record<Sector, number>;
    /** Per frame, the furthest elevation from the horizontal that still found a usable step, per side and half. */
    usableReachDeg: Record<Reach, DiagStat | null>;
  };
}

function eyeCapture(es: readonly EyeFrameDiagnostics[]): EyeCaptureDiagnostics {
  const s = (f: (e: EyeFrameDiagnostics) => number | null) => diagStat(es.map(f));
  const sectorMedianRadiusPx = Object.fromEntries(
    SECTORS.map((k) => {
      const xs = es.flatMap((e) => e.sectorRadii[k]);
      return [k, xs.length >= 10 ? medianOf(xs) : null];
    }),
  ) as Record<Sector, number | null>;
  const present = Object.values(sectorMedianRadiusPx).filter((x): x is number => x !== null);
  const range = present.length >= 2 ? Math.max(...present) - Math.min(...present) : null;
  const radius = med(es.map((e) => e.refinedRadiusPx));
  return {
    fromLandmarkIrisAShare:
      es.filter((e) => e.landmarkIris === 'A').length / Math.max(1, es.length),
    seedRadiusPx: s((e) => e.seedRadiusPx),
    refinedRadiusPx: s((e) => e.refinedRadiusPx),
    refinedToSeedRadiusRatio: s((e) => e.refinedToSeedRadiusRatio),
    seedCentreX: s((e) => e.seedCentre[0]),
    seedCentreY: s((e) => e.seedCentre[1]),
    refinedCentreX: s((e) => e.refinedCentre[0]),
    refinedCentreY: s((e) => e.refinedCentre[1]),
    refinedCentreMinusSeedX: s((e) => e.refinedCentreMinusSeed[0]),
    refinedCentreMinusSeedY: s((e) => e.refinedCentreMinusSeed[1]),
    refinedCentreShiftPx: s((e) => e.refinedCentreShiftPx),
    nasalRadiusFromSeedCentrePx: s((e) => e.nasalRadiusFromSeedCentrePx),
    temporalRadiusFromSeedCentrePx: s((e) => e.temporalRadiusFromSeedCentrePx),
    nasalRadiusFromRefinedCentrePx: s((e) => e.nasalRadiusFromRefinedCentrePx),
    temporalRadiusFromRefinedCentrePx: s((e) => e.temporalRadiusFromRefinedCentrePx),
    nasalMinusTemporalPx: s(
      (e) => e.nasalRadiusFromRefinedCentrePx - e.temporalRadiusFromRefinedCentrePx,
    ),
    nasalMinusTemporalFromSeedPx: s((e) =>
      e.nasalRadiusFromSeedCentrePx === null || e.temporalRadiusFromSeedCentrePx === null
        ? null
        : e.nasalRadiusFromSeedCentrePx - e.temporalRadiusFromSeedCentrePx,
    ),
    edgeAmbiguity: ambiguity(es.map((e) => e.rays)),
    edgeDistribution: {
      fitResidualPx: s((e) => e.fitResidualPx),
      usableArc: s((e) => e.usableArc),
      edgeBlurPx: s((e) => e.edgeBlurPx),
      edgeContrast: s((e) => e.edgeContrast),
      edgeRadiusPx: diagStat(es.flatMap((e) => e.edgeRadii)),
      withinFrameRobustSdPx: s((e) => diagStat(e.edgeRadii)?.robustSd ?? null),
      sectorMedianRadiusPx,
      sectorRangePx: range,
      sectorRangeVsRadius: range !== null && radius ? range / radius : null,
    },
    eyePosition: {
      eyeInnerCornerX: s((e) => e.eyeInnerCorner?.[0] ?? null),
      eyeInnerCornerY: s((e) => e.eyeInnerCorner?.[1] ?? null),
      eyeOuterCornerX: s((e) => e.eyeOuterCorner?.[0] ?? null),
      eyeOuterCornerY: s((e) => e.eyeOuterCorner?.[1] ?? null),
      eyeWidthPx: s((e) => e.eyeWidthPx),
      irisHorizontalPosition01: s((e) => e.irisHorizontalPosition01),
      seedHorizontalPosition01: s((e) => e.seedHorizontalPosition01),
      irisVerticalPosition01: s((e) => e.irisVerticalPosition01),
      seedVerticalPosition01: s((e) => e.seedVerticalPosition01),
    },
    lid: {
      lidOpeningPx: s((e) => e.lidOpeningPx),
      lidOpeningNormalizedToEyeWidth: s((e) => e.lidOpeningNormalizedToEyeWidth),
      blinkScore: s((e) => e.blinkScore),
      opennessToIrisDiameter: s((e) => e.opennessToIrisDiameter),
      upperLidToSeedPx: s((e) => e.upperLidToSeedPx),
      lowerLidToSeedPx: s((e) => e.lowerLidToSeedPx),
      upperLidToSeedNorm: s((e) => e.upperLidToSeedNorm),
      lowerLidToSeedNorm: s((e) => e.lowerLidToSeedNorm),
    },
    visibility: {
      sectorCoverage: Object.fromEntries(
        SECTORS.map((k) => {
          const rays = es.reduce((t, e) => t + (e.sectorRays?.[k]?.rays ?? 0), 0);
          const usable = es.reduce((t, e) => t + (e.sectorRays?.[k]?.usable ?? 0), 0);
          return [k, rays ? usable / rays : null];
        }),
      ) as Record<Sector, number | null>,
      sectorRays: Object.fromEntries(
        SECTORS.map((k) => [k, es.reduce((t, e) => t + (e.sectorRays?.[k]?.rays ?? 0), 0)]),
      ) as Record<Sector, number>,
      usableReachDeg: Object.fromEntries(
        REACHES.map((k) => [k, s((e) => e.usableReachDeg?.[k] ?? null)]),
      ) as Record<Reach, DiagStat | null>,
    },
  };
}

const HALF_KEYS = [
  'rawPdMm',
  'ipdPx',
  'ipdOverOuterCanthi',
  'irisARefinedToSeedRatio',
  'irisBRefinedToSeedRatio',
  'irisARefinedRadiusPx',
  'irisBRefinedRadiusPx',
  'irisACentreShiftPx',
  'irisBCentreShiftPx',
  'irisARefinedCentreMinusSeedX',
  'irisBRefinedCentreMinusSeedX',
  'irisAHorizontalPosition01',
  'irisBHorizontalPosition01',
  'irisAVerticalPosition01',
  'irisBVerticalPosition01',
  'irisAMultipleCandidateShare',
  'irisBMultipleCandidateShare',
  'irisARunnerUpWithin10PctShare',
  'irisBRunnerUpWithin10PctShare',
  'irisAChosenStepContrast',
  'irisBChosenStepContrast',
  'irisALidOpeningNormalized',
  'irisBLidOpeningNormalized',
] as const;

export type HalfKey = (typeof HALF_KEYS)[number];
export type HalfDiagnostics = { frames: number } & Record<HalfKey, number | null>;

function half(frames: readonly { m: CardlessMeasurement; d: FrameDiagnostics }[]): HalfDiagnostics {
  const eye = (k: 'a' | 'b') => frames.map((f) => f.d[k]);
  const amb = (k: 'a' | 'b') => ambiguity(eye(k).map((e) => e.rays));
  const ambA = amb('a');
  const ambB = amb('b');
  const m = (k: 'a' | 'b', f: (e: EyeFrameDiagnostics) => number | null) => med(eye(k).map(f));
  return {
    frames: frames.length,
    rawPdMm: med(frames.map((f) => f.m.convergedPdMm)),
    ipdPx: med(frames.map((f) => f.m.ipdPx)),
    ipdOverOuterCanthi: med(frames.map((f) => f.d.ipdOverOuterCanthi)),
    irisARefinedToSeedRatio: m('a', (e) => e.refinedToSeedRadiusRatio),
    irisBRefinedToSeedRatio: m('b', (e) => e.refinedToSeedRadiusRatio),
    irisARefinedRadiusPx: m('a', (e) => e.refinedRadiusPx),
    irisBRefinedRadiusPx: m('b', (e) => e.refinedRadiusPx),
    irisACentreShiftPx: m('a', (e) => e.refinedCentreShiftPx),
    irisBCentreShiftPx: m('b', (e) => e.refinedCentreShiftPx),
    irisARefinedCentreMinusSeedX: m('a', (e) => e.refinedCentreMinusSeed[0]),
    irisBRefinedCentreMinusSeedX: m('b', (e) => e.refinedCentreMinusSeed[0]),
    irisAHorizontalPosition01: m('a', (e) => e.irisHorizontalPosition01),
    irisBHorizontalPosition01: m('b', (e) => e.irisHorizontalPosition01),
    irisAVerticalPosition01: m('a', (e) => e.irisVerticalPosition01),
    irisBVerticalPosition01: m('b', (e) => e.irisVerticalPosition01),
    irisAMultipleCandidateShare: ambA.multipleCandidateShare,
    irisBMultipleCandidateShare: ambB.multipleCandidateShare,
    irisARunnerUpWithin10PctShare: ambA.fractionWhereRunnerUpIsWithin.pct10,
    irisBRunnerUpWithin10PctShare: ambB.fractionWhereRunnerUpIsWithin.pct10,
    irisAChosenStepContrast: ambA.chosenStepContrast?.median ?? null,
    irisBChosenStepContrast: ambB.chosenStepContrast?.median ?? null,
    irisALidOpeningNormalized: m('a', (e) => e.lidOpeningNormalizedToEyeWidth),
    irisBLidOpeningNormalized: m('b', (e) => e.lidOpeningNormalizedToEyeWidth),
  };
}

export interface CaptureDiagnostics {
  diagnosticsRevision: typeof DIAGNOSTICS_REVISION;
  /** Always "none": nothing in the measurement reads these numbers. */
  measurementUse: 'none';
  conventions: Record<string, string>;
  /** Used frames summarised here, and used frames that carried no diagnostics. */
  frames: number;
  framesWithoutDiagnostics: number;
  landmarkAIsLeftInImageShare: number;
  /** MediaPipe's face width over the used frames, px: face scale per window. */
  faceWidthPx: DiagStat | null;
  irisA: EyeCaptureDiagnostics;
  irisB: EyeCaptureDiagnostics;
  crossEye: {
    irisARefinedToSeedRatio: number | null;
    irisBRefinedToSeedRatio: number | null;
    refinedToSeedRatioDifference: number | null;
    refinedToSeedRatioRatio: number | null;
    pdFromAMm: DiagStat | null;
    pdFromBMm: DiagStat | null;
    pdFromBMinusAMm: DiagStat | null;
    ipdOverOuterCanthi: DiagStat | null;
    ipdOverInnerCanthi: DiagStat | null;
    irisAOverEyeWidthA: DiagStat | null;
    irisBOverEyeWidthB: DiagStat | null;
  };
  /** The used frames in capture order, split where the stability check splits them. */
  halves: {
    first: HalfDiagnostics;
    second: HalfDiagnostics;
    secondMinusFirst: Record<HalfKey, number | null>;
  };
}

export const CONVENTIONS: Record<string, string> = {
  imageSides:
    'A is the iris on the left of the image and B the one on the right; they are not OD/OS.',
  positions: 'px from the frame centre, x to the right and y down.',
  rayRadii: "px along each lateral ray from the centre of the refinement's last pass.",
  runnerUp:
    'On a ray with a chosen step, the other step passing the same tests with the highest contrast; the rule takes the outermost step, so the runner-up always lies inside it.',
  within:
    "Share of rays with a chosen step whose runner-up reaches at least 95, 90 or 80% of the chosen step's contrast; it may exceed it.",
  outerNearMiss:
    "Share of rays with a step outside the chosen one that passed every test except strength against the ray's strongest step.",
  position01:
    'Horizontal: 0 at the inner eye corner, 1 at the outer. Vertical: 0 at the upper lid, 1 at the lower, at the iris centre.',
  sectors:
    "Nasal is towards the other eye. Upper and lower are above and below the eyes' horizontal; Near is within 25° of it, Far 25–50°.",
  seedLids:
    "Seed vertical position and the lid distances use MediaPipe's own iris centre, not the refined one: upper and lower lid distances are measured across the eye at the seed's place along it, in px and over the eye width.",
  visibility:
    "Sector coverage is the share of the refinement's last-pass lateral rays in a sector that found a usable step; reach is the furthest elevation from the eyes' horizontal that still did, in degrees.",
};

/** The used frames' diagnostics, summarised; null when none of them carried any. It reads the frames and changes nothing. */
export function captureDiagnostics(
  used: readonly CardlessMeasurement[],
): CaptureDiagnostics | null {
  const frames = used.flatMap((m) => (m.diagnostics ? [{ m, d: m.diagnostics }] : []));
  if (!frames.length) return null;
  const a = frames.map((f) => f.d.a);
  const b = frames.map((f) => f.d.b);
  const n = frames.length;
  const cut = Math.floor(n / 2);
  const first = half(frames.slice(0, cut));
  const second = half(frames.slice(cut));
  const ratioA = med(a.map((e) => e.refinedToSeedRadiusRatio));
  const ratioB = med(b.map((e) => e.refinedToSeedRadiusRatio));
  return {
    diagnosticsRevision: DIAGNOSTICS_REVISION,
    measurementUse: 'none',
    conventions: CONVENTIONS,
    frames: n,
    framesWithoutDiagnostics: used.length - n,
    landmarkAIsLeftInImageShare: frames.filter((f) => f.d.landmarkAIsLeftInImage).length / n,
    faceWidthPx: diagStat(frames.map((f) => f.d.faceWidthPx ?? null)),
    irisA: eyeCapture(a),
    irisB: eyeCapture(b),
    crossEye: {
      irisARefinedToSeedRatio: ratioA,
      irisBRefinedToSeedRatio: ratioB,
      refinedToSeedRatioDifference: ratioA !== null && ratioB !== null ? ratioA - ratioB : null,
      refinedToSeedRatioRatio: ratioA !== null && ratioB ? ratioA / ratioB : null,
      pdFromAMm: diagStat(frames.map((f) => f.d.pdFromAMm)),
      pdFromBMm: diagStat(frames.map((f) => f.d.pdFromBMm)),
      pdFromBMinusAMm: diagStat(frames.map((f) => f.d.pdFromBMm - f.d.pdFromAMm)),
      ipdOverOuterCanthi: diagStat(frames.map((f) => f.d.ipdOverOuterCanthi)),
      ipdOverInnerCanthi: diagStat(frames.map((f) => f.d.ipdOverInnerCanthi)),
      irisAOverEyeWidthA: diagStat(
        a.map((e) => (e.eyeWidthPx ? (2 * e.refinedRadiusPx) / e.eyeWidthPx : null)),
      ),
      irisBOverEyeWidthB: diagStat(
        b.map((e) => (e.eyeWidthPx ? (2 * e.refinedRadiusPx) / e.eyeWidthPx : null)),
      ),
    },
    halves: {
      first,
      second,
      secondMinusFirst: Object.fromEntries(
        HALF_KEYS.map((k) => {
          const x = first[k];
          const y = second[k];
          return [k, x === null || y === null ? null : y - x];
        }),
      ) as Record<HalfKey, number | null>,
    },
  };
}
